import React, { useEffect, useState, useRef } from 'react';
import MonthlyStatus from './MonthlyStatus';

/**
 * Adapter component that transforms API data into the format expected by MonthlyStatus
 * with robust handling of tab changes and data nullification
 */
const MonthlyStatusAdapter = ({
  data,
  currentMonth,
  onPreviousMonth,
  onNextMonth,
  errors,
  isLoading,
  isActualCurrentMonth: parentIsActualCurrentMonth,
  hasMoreHistory: parentHasMoreHistory,
  hasNextMonthData: parentHasNextMonthData
}) => {
  // Keep a local state of the transformed data to prevent it from being lost
  const [adaptedData, setAdaptedData] = useState(null);
  
  // Use a ref to persist data across renders even when input becomes null
  const lastValidDataRef = useRef(null);
  
  // Store the current month in a ref to detect changes
  const currentMonthRef = useRef(currentMonth);
  
  // Track tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log("Tab became visible again - checking for cached data");
        
        // If we have a current month and no adapted data, try to restore from cache
        if (currentMonth && !adaptedData && !lastValidDataRef.current) {
          try {
            const cachedDataString = sessionStorage.getItem(`monthlyData_${currentMonth}`);
            if (cachedDataString) {
              const cachedData = JSON.parse(cachedDataString);
              console.log("Restored data from sessionStorage after tab change:", cachedData);
              setAdaptedData(cachedData);
              lastValidDataRef.current = cachedData;
            }
          } catch (error) {
            console.error("Error restoring data from cache:", error);
          }
        }
      }
    };
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentMonth, adaptedData]);
  
  // Get the previous month in YYYY-MM format
  const getPreviousMonth = (yearMonth) => {
    if (!yearMonth) return null;
    
    // Parse the current month to get the previous month
    const [year, month] = yearMonth.split('-').map(num => parseInt(num, 10));
    
    // Create a date for the current month
    const date = new Date(year, month - 1, 1); // month-1 because Date is 0-indexed
    
    // Subtract 1 month to get to the previous month
    date.setMonth(date.getMonth() - 1);
    
    // Format the result back to YYYY-MM
    const prevYear = date.getFullYear();
    const prevMonth = String(date.getMonth() + 1).padStart(2, '0');
    
    return `${prevYear}-${prevMonth}`;
  };

  // Get the next month in YYYY-MM format
  const getNextMonth = (yearMonth) => {
    if (!yearMonth) return null;
    
    // Parse the current month to get the next month
    const [year, month] = yearMonth.split('-').map(num => parseInt(num, 10));
    
    // Create a date for the current month
    const date = new Date(year, month - 1, 1); // month-1 because Date is 0-indexed
    
    // Add 1 month to get to the next month
    date.setMonth(date.getMonth() + 1);
    
    // Format the result back to YYYY-MM
    const nextYear = date.getFullYear();
    const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
    
    return `${nextYear}-${nextMonth}`;
  };

  // Check if today is the first day of the month
  const isFirstDayOfMonth = () => {
    const today = new Date();
    return today.getDate() === 1;
  };

  // Get the current year-month
  const getCurrentYearMonth = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  // State for tracking current month's actual month status
  const [isCurrentMonth, setIsCurrentMonth] = useState(true);
  
  // Update isCurrentMonth whenever currentMonth changes or parent prop changes
  useEffect(() => {
    // If provided by parent, use that value
    if (parentIsActualCurrentMonth !== undefined) {
      setIsCurrentMonth(parentIsActualCurrentMonth);
    } else {
      // Otherwise calculate it
      if (!currentMonth) {
        setIsCurrentMonth(true);
      } else {
        const today = new Date();
        const currentYearMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        
        // On first day of month, previous month is considered "current" for UI purposes
        // unless we explicitly have data for the current month
        if (today.getDate() === 1 && currentMonth === getPreviousMonth(currentYearMonth)) {
          setIsCurrentMonth(true);
        } else {
          setIsCurrentMonth(currentMonth === currentYearMonth);
        }
      }
    }
  }, [currentMonth, parentIsActualCurrentMonth]);
  
  // State for tracking previous month data availability
  const [hasPreviousMonthData, setHasPreviousMonthData] = useState(false);
  
  // State for tracking next month data availability
  const [hasNextMonthData, setHasNextMonthData] = useState(false);
  
  // Update next month data availability whenever the parent prop changes
  useEffect(() => {
    if (parentHasNextMonthData !== undefined) {
      setHasNextMonthData(parentHasNextMonthData);
    } else {
      // Check for next month data availability in sessionStorage if not provided by parent
      if (!currentMonth) {
        setHasNextMonthData(false);
        return;
      }
      
      const nextMonth = getNextMonth(currentMonth);
      const today = new Date();
      const actualCurrentMonth = getCurrentYearMonth();
      
      // If next month is in the future, there's no data
      if (nextMonth > actualCurrentMonth) {
        setHasNextMonthData(false);
        return;
      }
      
      try {
        const nextMonthDataKey = `monthlyData_${nextMonth}`;
        const cachedDataString = sessionStorage.getItem(nextMonthDataKey);
        
        if (!cachedDataString) {
          console.log(`MonthlyStatusAdapter: No cached data found for next month ${nextMonth}`);
          setHasNextMonthData(false);
          return;
        }
        
        const cachedData = JSON.parse(cachedDataString);
        
        // Check if the data is actually valid and usable
        const hasValidData = cachedData && 
          (cachedData.energycontrol || cachedData.aircontrol) &&
          !(cachedData.energycontrol && 
            cachedData.energycontrol.aggregate_status === 'none_found');
        
        console.log(`MonthlyStatusAdapter: Next month ${nextMonth} has valid data: ${hasValidData}`);
        setHasNextMonthData(hasValidData);
      } catch (error) {
        console.error('Error checking next month data:', error);
        setHasNextMonthData(false);
      }
    }
  }, [currentMonth, parentHasNextMonthData]);
  
  // Update previous month data availability whenever current month changes
  useEffect(() => {
    // First, check if the parent provided the flag
    if (parentHasMoreHistory !== undefined) {
      console.log(`MonthlyStatusAdapter: Using parent hasMoreHistory: ${parentHasMoreHistory}`);
      setHasPreviousMonthData(parentHasMoreHistory);
      return;
    }
    
    // Otherwise check sessionStorage directly
    if (!currentMonth) {
      setHasPreviousMonthData(false);
      return;
    }
    
    const prevMonth = getPreviousMonth(currentMonth);
    if (!prevMonth) {
      setHasPreviousMonthData(false);
      return;
    }
    
    try {
      // Check if previous month data exists in sessionStorage
      const prevMonthDataKey = `monthlyData_${prevMonth}`;
      const cachedDataString = sessionStorage.getItem(prevMonthDataKey);
      
      if (!cachedDataString) {
        console.log(`MonthlyStatusAdapter: No cached data found for ${prevMonth}`);
        setHasPreviousMonthData(false);
        return;
      }
      
      const cachedData = JSON.parse(cachedDataString);
      
      // Check if the data is actually valid and usable
      const hasValidData = cachedData && 
        (cachedData.energycontrol || cachedData.aircontrol) &&
        !(cachedData.energycontrol && 
          cachedData.energycontrol.aggregate_status === 'none_found');
      
      console.log(`MonthlyStatusAdapter: Previous month ${prevMonth} has valid data: ${hasValidData}`);
      setHasPreviousMonthData(hasValidData);
    } catch (error) {
      console.error('Error checking previous month data:', error);
      setHasPreviousMonthData(false);
    }
  }, [currentMonth, parentHasMoreHistory]);
  
  // When current month changes, attempt to restore from cache if we don't have data
  useEffect(() => {
    if (currentMonth && currentMonth !== currentMonthRef.current) {
      currentMonthRef.current = currentMonth;
      
      // If we don't have data for the new month, try to get it from cache
      if (!data && !adaptedData) {
        try {
          const cachedDataString = sessionStorage.getItem(`monthlyData_${currentMonth}`);
          if (cachedDataString) {
            const cachedData = JSON.parse(cachedDataString);
            console.log(`Restoring data for month ${currentMonth} from cache:`, cachedData);
            setAdaptedData(cachedData);
            lastValidDataRef.current = cachedData;
          }
        } catch (error) {
          console.error(`Error restoring data for ${currentMonth} from cache:`, error);
        }
      }
    }
  }, [currentMonth, data, adaptedData]);
  
  // Transform the data when it changes
  useEffect(() => {
    console.log("MonthlyStatusAdapter processing data for month:", currentMonth, data);
    
    // If data is null, preserve the last valid data in the ref but don't update state
    if (!data) {
      console.log("Received null data, preserving last valid data", lastValidDataRef.current);
      
      // Special case for first day of month - try to show previous month data
      const today = new Date();
      const actualCurrentMonth = getCurrentYearMonth();
      
      if (today.getDate() === 1 && currentMonth === actualCurrentMonth) {
        console.log("First day of month with null data - attempting to show previous month");
        const prevMonth = getPreviousMonth(currentMonth);
        
        try {
          const prevMonthDataString = sessionStorage.getItem(`monthlyData_${prevMonth}`);
          if (prevMonthDataString) {
            const prevMonthData = JSON.parse(prevMonthDataString);
            setAdaptedData(prevMonthData);
            lastValidDataRef.current = prevMonthData;
            console.log("Set previous month data on first day:", prevMonthData);
          }
        } catch (error) {
          console.error("Error retrieving previous month data:", error);
        }
      }
      
      return;
    }
    
    // If data is already in the expected format, use it directly
    if ((data.energycontrol && typeof data.energycontrol === 'object' && 
        (data.energycontrol.metadata || data.energycontrol.spot_comparison)) ||
        (data.aircontrol && typeof data.aircontrol === 'object' && 
        (data.aircontrol.co2 || data.aircontrol.temperature))) {
      console.log("Data is already in the expected format");
      setAdaptedData(data);
      lastValidDataRef.current = data; // Store in ref for persistence
      return;
    }
    
    // Create a container for the transformed data
    const transformedData = {};
    
    // Special case for first day of month - we might need to check for both current month and previous month
    const today = new Date();
    const isFirstDay = today.getDate() === 1;
    const actualCurrentMonth = getCurrentYearMonth();
    const prevMonth = isFirstDay ? getPreviousMonth(actualCurrentMonth) : null;
    
    // Handle energycontrol data
    if (data.energycontrol) {
      // Check if energycontrol has the expected structure
      if (data.energycontrol.results) {
        // On first day of month, prefer current month data if available, otherwise use previous month
        if (isFirstDay && currentMonth === actualCurrentMonth && 
            !data.energycontrol.results[currentMonth]?.data && 
            data.energycontrol.results[prevMonth]?.data) {
          console.log(`First day of month: Using previous month (${prevMonth}) data`);
          transformedData.energycontrol = data.energycontrol.results[prevMonth].data;
        }
        // Normal case - use current month data if available
        else if (data.energycontrol.results[currentMonth]?.data) {
          transformedData.energycontrol = data.energycontrol.results[currentMonth].data;
        }
        // If no data for requested month, try to find any month that has data
        else {
          const anyFoundMonth = Object.entries(data.energycontrol.results)
            .find(([period, periodData]) => periodData.status === 'found' && periodData.data);
          
          if (anyFoundMonth) {
            console.log(`No data for ${currentMonth}, using data from ${anyFoundMonth[0]}`);
            transformedData.energycontrol = anyFoundMonth[1].data;
          }
        }
      } 
      // Check if data is directly in the energycontrol.data property
      else if (data.energycontrol.data && typeof data.energycontrol.data === 'object') {
        transformedData.energycontrol = data.energycontrol.data;
      }
      // Check if the data object itself is what we need (has metadata, etc)
      else if (data.energycontrol.metadata || data.energycontrol.spot_comparison || 
               data.energycontrol.heating_summary || data.energycontrol.consumption_summary) {
        transformedData.energycontrol = data.energycontrol;
      }
    }
    
    // Handle aircontrol data - similar logic with first day handling
    if (data.aircontrol) {
      if (data.aircontrol.results) {
        // On first day of month, prefer current month data if available, otherwise use previous month
        if (isFirstDay && currentMonth === actualCurrentMonth && 
            !data.aircontrol.results[currentMonth]?.data && 
            data.aircontrol.results[prevMonth]?.data) {
          console.log(`First day of month: Using previous month (${prevMonth}) aircontrol data`);
          transformedData.aircontrol = data.aircontrol.results[prevMonth].data;
        }
        // Normal case - use current month data if available
        else if (data.aircontrol.results[currentMonth]?.data) {
          transformedData.aircontrol = data.aircontrol.results[currentMonth].data;
        }
        // If no data for requested month, try to find any month that has data
        else {
          const anyFoundMonth = Object.entries(data.aircontrol.results)
            .find(([period, periodData]) => periodData.status === 'found' && periodData.data);
          
          if (anyFoundMonth) {
            console.log(`No aircontrol data for ${currentMonth}, using data from ${anyFoundMonth[0]}`);
            transformedData.aircontrol = anyFoundMonth[1].data;
          }
        }
      }
      else if (data.aircontrol.data && typeof data.aircontrol.data === 'object') {
        transformedData.aircontrol = data.aircontrol.data;
      }
      else if (data.aircontrol.co2 || data.aircontrol.temperature || data.aircontrol.humidity) {
        transformedData.aircontrol = data.aircontrol;
      }
    }
    
    // Only update if we have valid transformed data
    if (Object.keys(transformedData).length > 0) {
      console.log("Adapted data:", transformedData);
      setAdaptedData(transformedData);
      lastValidDataRef.current = transformedData; // Store in ref for persistence
      
      // Also ensure it's saved to sessionStorage for persistence across tab changes
      try {
        sessionStorage.setItem(`monthlyData_${currentMonth}`, JSON.stringify(transformedData));
      } catch (error) {
        console.error(`Error saving data for ${currentMonth} to sessionStorage:`, error);
      }
    } else {
      console.log("No valid data could be extracted");
      
      // Special case for first day - try to use previous month data from cache
      if (isFirstDay && currentMonth === actualCurrentMonth) {
        try {
          const prevMonthDataString = sessionStorage.getItem(`monthlyData_${prevMonth}`);
          if (prevMonthDataString) {
            const prevMonthData = JSON.parse(prevMonthDataString);
            console.log(`First day of month: No data available, using cached previous month data for ${prevMonth}`);
            setAdaptedData(prevMonthData);
            lastValidDataRef.current = prevMonthData;
          }
        } catch (error) {
          console.error("Error retrieving previous month data:", error);
        }
      }
    }
  }, [data, currentMonth]);

  // Better implementation - more explicitly handling null cases
  const determineDisplayData = () => {
    // If we have incoming data, use it
    if (data) return data;
    
    // If we have adapted data in state, use it
    if (adaptedData) return adaptedData;
    
    // If we have cached valid data in the ref, use it
    if (lastValidDataRef.current) return lastValidDataRef.current;
    
    // If we have currentMonth, try to get from sessionStorage
    if (currentMonth) {
      try {
        const cachedDataString = sessionStorage.getItem(`monthlyData_${currentMonth}`);
        if (cachedDataString) {
          const cachedData = JSON.parse(cachedDataString);
          // Update our state and ref for future use
          if (!adaptedData) setAdaptedData(cachedData);
          if (!lastValidDataRef.current) lastValidDataRef.current = cachedData;
          return cachedData;
        }
        
        // Special case for first day of month - if current month doesn't have data,
        // check if previous month's data is available
        const today = new Date();
        const actualCurrentMonth = getCurrentYearMonth();
        
        if (today.getDate() === 1 && currentMonth === actualCurrentMonth) {
          console.log("First day of month - checking for previous month data");
          const prevMonth = getPreviousMonth(currentMonth);
          const prevMonthDataString = sessionStorage.getItem(`monthlyData_${prevMonth}`);
          
          if (prevMonthDataString) {
            const prevMonthData = JSON.parse(prevMonthDataString);
            console.log("Found previous month data, using that on first day of month");
            // Don't update our state for this since it's just a fallback
            return prevMonthData;
          }
        }
      } catch (error) {
        console.error("Error retrieving cache in determineDisplayData:", error);
      }
    }
    
    // If all else fails, return null
    return null;
  };

  const displayData = determineDisplayData();
  
  // Log when the component renders and what data it has
  console.log("MonthlyStatusAdapter rendering with data for month:", currentMonth);
  console.log("Display data:", displayData);
  console.log("Previous month data available:", hasPreviousMonthData);
  console.log("Next month data available:", hasNextMonthData);
  
  return (
    <MonthlyStatus
      data={displayData}
      currentMonth={currentMonth}
      onPreviousMonth={onPreviousMonth}
      onNextMonth={onNextMonth}
      errors={errors}
      isLoading={isLoading}
      isActualCurrentMonth={isCurrentMonth}
      hasMoreHistory={hasPreviousMonthData}
      hasNextMonthData={hasNextMonthData}
    />
  );
};

export default MonthlyStatusAdapter;